import { Trans, useLingui } from "@lingui/react";
import { WIcon } from "@mobility-item-page/client";
import { Alert, Button, Modal, TextArea, TextField, Toggle } from "@warp-ds/react";
import { type SubmitEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { useValidDealerContext } from "../../context/DealerProviderContext.tsx";
import { useTracking } from "../../utils/pulseHelper.ts";
import { validatePhone } from "../../utils/validatePhone.ts";
import { CannedMessageGroup } from "../CannedMessageGroup.tsx";
import { openContactFormEventId } from "./ContactFormModal.tsx";
import { MinimizedModal } from "./MinimizedModal.tsx";

enum ContactFormState {
  Initial,
  Submitting,
  Success,
  InvalidEid,
  InvalidLogin,
  Error,
}

enum DisplayState {
  Hidden,
  Minimized,
  Open,
}

const empty = (message: string) => message.trim().length === 0;

export const ContactFormModalv2 = () => {
  const [display, setDisplay] = useState<DisplayState>(DisplayState.Hidden);
  const [formState, setFormState] = useState<ContactFormState>(ContactFormState.Initial);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [errorCode, setErrorCode] = useState(0);
  const [callMeBack, setCallMeBack] = useState(false);
  const focusRef = useRef<HTMLInputElement>(null);
  const { i18n } = useLingui();
  const {
    trackViewContactFormModal,
    trackCancelContactFormModal,
    trackSubmitContactForm,
    trackMinimizeContactFormModal,
  } = useTracking();

  const {
    organisationData: { name },
    itemData: { adId },
    external: { isMobile, brand, adViewType },
  } = useValidDealerContext();

  const triggerCloseOrMinimize = useCallback(() => {
    if (message !== "" && !isMobile && display === DisplayState.Open) {
      setDisplay(DisplayState.Minimized);
      trackMinimizeContactFormModal();
    } else {
      setDisplay(DisplayState.Hidden);
      if (formState !== ContactFormState.Success) {
        trackCancelContactFormModal();
      }
    }
  }, [message, isMobile, display, trackCancelContactFormModal, trackMinimizeContactFormModal, formState]);

  useEffect(() => {
    const listener = () => {
      setDisplay(DisplayState.Open);
      trackViewContactFormModal({ isSuccessView: false, adViewType });
    };
    const escListener = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        triggerCloseOrMinimize();
        setTextHelpText("");
        setFormState(ContactFormState.Initial);
      }
    };

    if (formState === ContactFormState.Success) {
      trackViewContactFormModal({ isSuccessView: true, adViewType });
    }

    window.addEventListener(openContactFormEventId, listener);
    window.addEventListener("keydown", escListener);
    return () => {
      window.removeEventListener(openContactFormEventId, listener);
      window.removeEventListener("keydown", escListener);
    };
  }, [trackViewContactFormModal, formState, triggerCloseOrMinimize, adViewType]);

  const title = i18n.t({
    id: "dealer.contactForm.writeToSeller.title",
    message: "Write to {dealerName}",
    values: { dealerName: name },
    comment: "Header for the contact form",
  });

  const errorText = i18n.t({
    id: "dealer.contactForm.errorText.generic",
    message: "An error occurred while sending your message.\n" + "Please try again later. (Error code: {errorCode})",
    values: { errorCode },
    comment:
      "Generic error text displayed when an unexpected error occurs while submitting the contact form, includes error code",
  });
  const [textHelpText, setTextHelpText] = useState("");
  const [phoneHelpText, setPhoneHelpText] = useState("");
  const formId = useId();

  const validateMessage = (): boolean => {
    if (!message) {
      setTextHelpText(
        i18n.t({
          id: "dealer.contactForm.textArea.helpText.mandatory",
          message: "Must be filled out",
          comment: "Help text when field has changed and has no value, displayed underneath the text-area",
        }),
      );
      return false;
    }
    if (empty(message)) {
      setTextHelpText(
        i18n.t({
          id: "dealer.contactForm.textArea.helpText.emptyText",
          message: "Enter a message for the seller",
          comment:
            "Help text when the field has changed and is empty except spaces, displayed underneath the text-area",
        }),
      );
      setMessage("");
      return false;
    }
    setTextHelpText("");
    return true;
  };

  const validatePhoneNo = (): boolean => {
    if (!validatePhone(phoneNumber, brand)) {
      setPhoneHelpText(
        i18n.t({
          id: "dealer.contactForm.phone.helpText.invalid",
          message: "Enter a valid phone number",
          comment: "Help text when the phone number format is invalid, displayed underneath the phone number field",
        }),
      );
      return false;
    }
    setPhoneHelpText("");
    return true;
  };

  const handleMessageChange = (newMessage: string) => {
    setFormState(ContactFormState.Initial);
    setTextHelpText("");
    setMessage(newMessage);
  };

  const handlePhoneChange = (newPhone: string) => {
    setFormState(ContactFormState.Initial);
    setPhoneHelpText("");
    setPhoneNumber(newPhone.trim().replace(/\s/g, ""));
  };

  const validate = () => {
    const validMessage = validateMessage();
    const validPhone = callMeBack ? validatePhoneNo() : true;
    return validPhone && validMessage;
  };

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    const isValid = validate();

    if (!isValid) {
      return;
    } else {
      setFormState(ContactFormState.Submitting);
    }

    try {
      const response = await fetch(`/mobility/api/contact/${adId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message,
          phoneNumber: callMeBack ? phoneNumber : "",
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 401) {
          if (data.message === "Not logged in") {
            setFormState(ContactFormState.InvalidLogin);
            return;
          } else if (data.message === "eID verification failed") {
            setFormState(ContactFormState.InvalidEid);
            return;
          } else {
            setErrorCode(response.status);
            throw new Error(`Failed to send message: ${response.status} ${response.statusText} ${data.message}`);
          }
        } else {
          setErrorCode(response.status);
          throw new Error(`Failed to send message: ${response.status} ${response.statusText} ${data.message}`);
        }
      }

      trackSubmitContactForm(phoneNumber);
      // Success - reset form and show success view
      setMessage("");
      setPhoneNumber("");
      setFormState(ContactFormState.Success);
    } catch (error) {
      console.error("Error sending contact form:", error);
      setFormState(ContactFormState.Error);
    }
  };

  return (
    <>
      <Modal
        open={display === DisplayState.Open}
        title={title}
        right={
          <Button
            pill
            aria-label={
              message !== "" && !isMobile && display === DisplayState.Open
                ? i18n.t({
                    id: "dealer.modal.minimize.button",
                    message: "Minimize",
                    comment: "Aria label for the minimize button in modal",
                  })
                : i18n.t({
                    id: "dealer.modal.close.button",
                    message: "Close",
                    comment: "Aria label for the close button in modal",
                  })
            }
            onClick={() => {
              triggerCloseOrMinimize();
              setTextHelpText("");
              setPhoneHelpText("");
              setFormState(ContactFormState.Initial);
            }}
            disabled={formState === ContactFormState.Submitting}
            className="-mr-8 sm:-mr-12"
          >
            {message !== "" && !isMobile && display === DisplayState.Open ? (
              <WIcon name="Minus" size="24px" />
            ) : (
              <WIcon name="Close" size="24px" />
            )}
          </Button>
        }
        footer={
          formState !== ContactFormState.Success ? (
            <Button
              primary
              type="submit"
              form={formId}
              loading={formState === ContactFormState.Submitting}
              fullWidth={isMobile}
            >
              <Trans id="dealer.contactForm.submitButton.text" message="Send message" />
            </Button>
          ) : (
            <Button
              fullWidth={isMobile}
              className="self-end"
              onClick={() => {
                setDisplay(DisplayState.Hidden);
                setFormState(ContactFormState.Initial);
              }}
            >
              <Trans id="dealer.modal.close.button" message="Close" />
            </Button>
          )
        }
      >
        {formState !== ContactFormState.Success ? (
          <form id={formId} onSubmit={handleSubmit}>
            <TextArea
              label={i18n.t({
                id: "dealer.contactForm.textArea.label",
                message: "Message to the seller",
                comment: "Label for the message, explains that this text will be sent to the advertiser",
              })}
              placeholder={i18n.t({
                id: "dealer.contactForm.textArea.placeholder",
                message: "Write a message…",
                comment: "Placeholder for the message text area",
              })}
              value={message}
              disabled={formState === ContactFormState.Submitting}
              invalid={textHelpText !== ""}
              helpText={textHelpText}
              aria-errormessage={textHelpText !== "" ? textHelpText : undefined}
              minimumRows={5}
              className="mb-8"
              onChange={({ target: { value: newValue } }) => {
                handleMessageChange(newValue);
              }}
            />
            <CannedMessageGroup message={message} setMessage={handleMessageChange} />
            <Toggle
              className={`mt-24 ${callMeBack ? "mb-16" : "mb-24"}`}
              type="checkbox"
              checked={callMeBack}
              onChange={(checked) => setCallMeBack(checked)}
              label={i18n.t({ id: "dealer.contactForm.callMe.label", message: "I would like to be called." })}
            />
            {callMeBack && (
              <TextField
                label={i18n.t({
                  id: "dealer.contactForm.phone.label",
                  message: "Phone number",
                  comment: "Label for the phone number text field",
                })}
                placeholder={i18n.t({
                  id: "dealer.contactForm.phone.placeholder",
                  message: "Enter your phone number",
                  comment:
                    "Placeholder for the phone number field, using the country code for each country. Only the default (english) is a text",
                })}
                type="tel"
                disabled={formState === ContactFormState.Submitting}
                invalid={phoneHelpText !== ""}
                helpText={phoneHelpText}
                aria-errormessage={phoneHelpText !== "" ? phoneHelpText : undefined}
                ref={focusRef}
                value={phoneNumber}
                onChange={({ target: { value } }) => handlePhoneChange(value)}
                className={"mb-24"}
              />
            )}
            <div className="mb-8 text-xs s-text-subtle">
              <Trans
                id="dealer.contactForm.infoText"
                message="When you send this message, your name, email, and phone number are transferred to the seller, who will then be responsible for your data. The message is sent via your registered email. {brand} may review and block messages to prevent fraud."
                values={{ brand: brand.toUpperCase() }}
              />
              <div className="mt-8">
                <Button link className="text-xs" href="/my-page/my-account" target="_blank">
                  <Trans id="dealer.contactForm.profileLink" message="You can view and change your e-mail here" />
                </Button>
              </div>
            </div>
            <Alert show={formState === ContactFormState.InvalidLogin} type="negative" className="mb-24">
              <h2 className="t4">
                <Trans
                  id="dealer.contactForm.loginErrorText.title"
                  message="You have been logged out"
                  comment="Alert title shown on invalid login error"
                />
              </h2>
              <p>
                <Trans
                  id="dealer.contactForm.loginErrorText.description"
                  message="It looks like your session has expired. Log in again to send your message to the seller."
                  comment="Alert description displayed when a user was unauthorised while submitting the contact form"
                />
              </p>
              <Button className="flex items-center gap-8" href="/auth/login" target="_blank">
                <Trans id="dealer.contactForm.loginErrorButton.text" message="Log in (opens in new window)" />
                <WIcon name="LinkExternal" />
              </Button>
            </Alert>
            <Alert show={formState === ContactFormState.InvalidEid} type="negative" className="mb-24">
              Invalid eID code here
            </Alert>
            <Alert show={formState === ContactFormState.Error} type="negative" className="mb-24">
              {errorText}
            </Alert>
          </form>
        ) : (
          <div className="flex flex-col gap-24">
            <Trans
              id="dealer.contactForm.success.text"
              message="Your message has been sent. The dealer will contact you as soon as possible."
              comment="Message shown when contact form is sent successfully"
            />
          </div>
        )}
      </Modal>
      {display === DisplayState.Minimized && (
        <MinimizedModal
          dealerName={name}
          maximize={() => setDisplay(DisplayState.Open)}
          close={() => setDisplay(DisplayState.Hidden)}
        />
      )}
    </>
  );
};
